// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Login gateway  (route /welcome — mobile number → SMS code → guest option)
// Header of the login page: 'Return to Store', logo, and the step tabs (WAI-ARIA tablist with
// arrow-key navigation). The OTP tab is disabled until a code has been sent.
// ─────────────────────────────────────────────────────────────────────────────
import { useRef, type KeyboardEvent } from "react";
import { ArrowLeft, MessageSquareText, ShieldCheck, User } from "lucide-react";
import { COPY, STEP_TABS, type StepId } from "../../data/welcome";

interface WelcomeHeaderProps {
  step: StepId;
  onStep: (step: StepId) => void;
  /** The OTP tab only means something once a code has been sent. */
  otpAvailable: boolean;
  signedIn: boolean;
  onReturnToStore: () => void;
}

export default function WelcomeHeader({ step, onStep, otpAvailable, signedIn, onReturnToStore }: WelcomeHeaderProps) {
  const tabRefs = useRef<Partial<Record<StepId, HTMLButtonElement | null>>>({});
  const enabled = STEP_TABS.filter((tab) => tab.id !== "otp" || otpAvailable);

  // Arrow keys / Home / End move between tabs (WAI-ARIA tabs pattern); Tab leaves the list.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = enabled.findIndex((tab) => tab.id === step);
    let next = index;
    if (event.key === "ArrowRight") next = (index + 1) % enabled.length;
    else if (event.key === "ArrowLeft") next = (index - 1 + enabled.length) % enabled.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = enabled.length - 1;
    else return;
    event.preventDefault();
    const target = enabled[next].id;
    onStep(target);
    tabRefs.current[target]?.focus();
  };

  return (
    <header className="border-b border-ocean-900/5 bg-white dark:border-white/10 dark:bg-ocean-900">
      <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-x-4 gap-y-3 px-4 py-3 sm:px-6 lg:min-h-[80px] lg:flex-nowrap">
        <button
          type="button"
          onClick={onReturnToStore}
          className="flex shrink-0 items-center gap-2 rounded-lg text-[13px] font-semibold text-ocean-950 hover:text-teal-700 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-600 dark:text-foam-50"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Return to Store
        </button>

        <span aria-hidden="true" className="hidden h-8 w-px bg-ocean-900/10 dark:bg-white/15 lg:block" />

        <div className="flex shrink-0 items-center gap-3">
          <img src="/logo.png" alt="" className="h-9 w-9 object-contain" />
          <div>
            <p className="font-display text-lg font-bold leading-none text-teal-700 dark:text-teal-300">ZenFresh</p>
            <p className="mt-1 hidden text-[10px] font-bold uppercase tracking-wider text-teal-700 dark:text-teal-300 sm:block">
              {COPY.brandTagline}
            </p>
          </div>
        </div>

        {/* The four "steps" of signing in double as tabs onto the panel on the right. */}
        <div
          role="tablist"
          aria-label="Sign-in steps"
          onKeyDown={handleKeyDown}
          className="order-last -mx-1 flex w-full gap-1 overflow-x-auto px-1 py-1 lg:order-none lg:mx-0 lg:w-auto lg:flex-1 lg:justify-center lg:overflow-visible"
        >
          {STEP_TABS.map((tab) => {
            const selected = tab.id === step;
            const disabled = tab.id === "otp" && !otpAvailable;
            return (
              <button
                key={tab.id}
                ref={(el) => {
                  tabRefs.current[tab.id] = el;
                }}
                type="button"
                role="tab"
                id={`welcome-tab-${tab.id}`}
                aria-selected={selected}
                aria-controls="welcome-panel"
                tabIndex={selected ? 0 : -1}
                disabled={disabled}
                onClick={() => onStep(tab.id)}
                className={`shrink-0 whitespace-nowrap rounded-full px-3 py-2 text-[13px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 disabled:cursor-not-allowed disabled:opacity-40 ${
                  selected
                    ? "bg-teal-700 text-white shadow-sm"
                    : "text-ocean-900 hover:bg-ocean-900/5 dark:text-foam-100 dark:hover:bg-white/10"
                }`}
              >
                {tab.label}
                {disabled && <span className="sr-only"> (available after you request a code)</span>}
              </button>
            );
          })}
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => onStep("help")}
            className="hidden items-center gap-1.5 rounded-full bg-teal-100 px-3 py-1.5 text-[11px] font-bold text-teal-700 hover:bg-teal-200 2xl:flex"
          >
            <MessageSquareText className="h-4 w-4" aria-hidden="true" />
            Live Concierge
          </button>
          <span className="hidden items-center gap-1.5 rounded-full bg-ocean-700/10 px-3 py-1.5 text-[11px] font-bold text-ocean-800 dark:bg-white/10 dark:text-foam-100 2xl:flex">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            Secure login
          </span>
          <button
            type="button"
            onClick={() => onStep("phone")}
            aria-label={signedIn ? "Your account" : "Log in with your mobile number"}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-700 text-white hover:bg-teal-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
          >
            <User className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </header>
  );
}
